import { track } from '@helpers/analytics';
import { wineryUrl } from '@helpers/vivinoUrls';
import { isPartnerWinery } from '@helpers/winery';
import {
  Button,
  ButtonTheme,
  Typography,
  TypographyType,
  useBreakpoint,
} from '@vivino/js-react-common-ui';
import { useAppContext, useI18N } from '@vivino/js-web-common';
import { ComponentDirection } from '@webtypes/common';
import { FullWinery } from '@webtypes/goApi';
import React, { useEffect, useState } from 'react';
import { fetchWinerySponsorship } from 'vivino-js/api/winerySponsorships';
import Card from 'vivino-ui/atoms/Card';

import TruncatedText, { NumTruncateLines } from '@components/TruncatedText/TruncatedText';
import WineryStats from '@components/WineryPage/components/WineryStats/WineryStats';
import WineryStatusText from '@components/WineryPage/components/WineryStatusText/WineryStatusText';

import styles from './meetTheWinery.module.scss';

const TRANSLATIONS_PATH = 'components.shared.winery_sponsorship';
const TRANSLATIONS = {
  partner_winery: `${TRANSLATIONS_PATH}.partner_winery`,
  wines_on_vivino: `${TRANSLATIONS_PATH}.wines_on_vivino`,
  average_rating: `${TRANSLATIONS_PATH}.average_rating`,
  discover_the_winery: `${TRANSLATIONS_PATH}.discover_the_winery`,
};

interface MeetTheWineryProps {
  vintageId: number;
  winery: FullWinery;
  redirectToWineryPage?: boolean;
}

const MeetTheWinery = ({ vintageId, winery }: MeetTheWineryProps) => {
  const { __PRELOADED_LOCALE__: locale } = useAppContext();

  const hasMeetTheWineryContent =
    winery && isPartnerWinery(winery) && winery.sponsored_entity !== null;

  const [sponsorship, setSponsorship] = useState(null);

  useEffect(() => {
    if (!hasMeetTheWineryContent) {
      return;
    }
    fetchWinerySponsorship({
      wineryId: winery.id,
    })
      .then((data) => {
        setSponsorship(data.sponsorship);
        track({
          event: 'Meet winery - View',
          screen: 'Wine',
          props: {
            vintageId,
            wineryId: winery.id,
          },
        });
      })
      .catch(() => {
        // Simply ignore and not show this module for errors
      });
  }, []);

  const { isTablet, isDesktop } = useBreakpoint();
  const { t } = useI18N();

  if (!winery || !sponsorship) {
    return null;
  }

  let imgSrc;

  if (sponsorship.image) {
    const { variations } = sponsorship.image;
    imgSrc = isTablet || isDesktop ? variations.medium : variations.small;
  }
  // Use image.variations doesn't seem to work on testing, so use image.location instead
  // See https://tickets.vivino.com/issues/40013
  const logoImgSrc = winery?.image?.location;

  const onClickReadMore = () => {
    track({
      event: 'Meet winery - Open',
      screen: 'Wine',
      props: {
        vintageId,
        wineryId: winery.id,
      },
    });
    globalThis.location.href = wineryUrl({ winery });
  };

  return (
    <>
      <Card className={styles.container} onClick={onClickReadMore}>
        <div className={styles.imageWrapper}>
          {imgSrc && <img src={imgSrc} className={styles.sponsoredImage} alt={winery.name} />}
          {logoImgSrc && <img src={logoImgSrc} className={styles.wineryLogo} />}
        </div>
        <div className={styles.textWrapper}>
          <Typography type={TypographyType.HeadlineLarge} className={styles.wineryName}>
            {winery.name}
          </Typography>
          <WineryStatusText isPartnerWinery />
          <div className={styles.statistics}>
            <WineryStats
              winery={winery}
              locale={locale}
              direction={ComponentDirection.Horizontal}
            />
          </div>
          <TruncatedText numLines={NumTruncateLines.Four}>
            <Typography type={TypographyType.BodyMedium} className={styles.extract}>
              {sponsorship.extract}
            </Typography>
          </TruncatedText>

          <div className={styles.readMore}>
            <Button className={styles.button} theme={ButtonTheme.Primary}>
              {t(TRANSLATIONS.discover_the_winery)}
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default MeetTheWinery;
